import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiClient } from "./api";
import { TOKEN_STORAGE_KEY } from "./auth-storage";

type AuthState =
  | { status: "unknown" }
  | { status: "anonymous" }
  | { status: "authenticated"; token: string };

interface AuthContextValue {
  state: AuthState;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<AuthState>({ status: "unknown" });

  // On first mount, see if we have a stored token and verify it. If the call
  // fails (expired/invalid), clear the token.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      setState({ status: "anonymous" });
      return;
    }
    apiClient.frontend
      .Me()
      .then(() => setState({ status: "authenticated", token }))
      .catch(() => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setState({ status: "anonymous" });
      });
  }, []);

  const login = useCallback(async (password: string) => {
    const res = await apiClient.frontend.Login({ Password: password });
    localStorage.setItem(TOKEN_STORAGE_KEY, res.Token);
    setState({ status: "authenticated", token: res.Token });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.frontend.Logout();
    } catch {
      // ignore — we're going to clear local state regardless
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setState({ status: "anonymous" });
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ state, login, logout }), [state, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
